import React from 'react';
import { useTranslation } from '@wix/yoshi-flow-editor';
import { Button, ButtonPriority, Text, TextTypography } from 'wix-ui-tpa/cssVars';
import { dataHooks } from 'root/dataHooks';
import type { MenuAddOnOrderingPageConflict } from 'root/components/MenuAddOn/types';
import { classes } from './Widget.st.css';

type OrderingPageConflictModalProps = {
  conflict?: MenuAddOnOrderingPageConflict | null;
  onClearCart?: () => Promise<void>;
  onClose?: () => void;
};

export const OrderingPageConflictModal = ({ conflict, onClearCart, onClose }: OrderingPageConflictModalProps) => {
  const { t } = useTranslation();
  const [isClearingCart, setIsClearingCart] = React.useState(false);

  if (!conflict) {
    return null;
  }

  const handleClearCart = async () => {
    setIsClearingCart(true);
    try {
      await onClearCart?.();
    } finally {
      setIsClearingCart(false);
    }
  };

  return (
    <div className={classes.orderingPageConflictOverlay} data-hook={dataHooks.menuAddOnWidget.orderingPageConflict}>
      <section
        aria-labelledby="menu-add-on-ordering-page-conflict-title"
        aria-modal="true"
        className={classes.orderingPageConflictModal}
        role="dialog"
      >
        <button
          aria-label={t('app.menu-add-on.widget.ordering-page-conflict.close')}
          className={classes.orderingPageConflictCloseButton}
          type="button"
          onClick={onClose}
        />
        <Text
          className={classes.orderingPageConflictTitle}
          id="menu-add-on-ordering-page-conflict-title"
          typography={TextTypography.largeTitle}
        >
          {t('app.menu-add-on.widget.ordering-page-conflict.title')}
        </Text>
        <Text className={classes.orderingPageConflictText} typography={TextTypography.runningText}>
          {t('app.menu-add-on.widget.ordering-page-conflict.description', {
            curOrderingPage: conflict.currentOrderingPage,
            prevOrderingPage: conflict.previousOrderingPage,
          })}
        </Text>
        <div className={classes.orderingPageConflictActions}>
          <Button
            upgrade
            className={classes.orderingPageConflictSecondaryButton}
            priority={ButtonPriority.secondary}
            type="button"
            onClick={onClose}
          >
            {t('app.menu-add-on.widget.ordering-page-conflict.keep-current-selection')}
          </Button>
          <Button
            upgrade
            className={classes.orderingPageConflictPrimaryButton}
            disabled={isClearingCart}
            priority={ButtonPriority.primary}
            type="button"
            onClick={handleClearCart}
          >
            {t('app.menu-add-on.widget.ordering-page-conflict.clear-cart')}
          </Button>
        </div>
      </section>
    </div>
  );
};
